import type {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { Switch } from '~shared/shadcn/ui/switch';

import { calculateDurationInMinutes } from './helpers';

const getWatchedTime = (watch: UseFormWatch<any>, field: string) =>
  (watch(field) || '') as string;

const getPrivateLessonMaxDurationMinutes = (watch: UseFormWatch<any>) => {
  const timeConfig = watch('timeConfig');
  if (timeConfig === 'different') {
    const days = (watch('days') ?? []) as string[];
    const dayTimes = watch('dayTimes') as
      | Record<string, { startTime?: string; endTime?: string }>
      | undefined;
    const fallbackStart = getWatchedTime(watch, 'times.startTime');
    const fallbackEnd = getWatchedTime(watch, 'times.endTime');

    const windows = days
      .map((day) =>
        calculateDurationInMinutes(
          dayTimes?.[day]?.startTime || fallbackStart,
          dayTimes?.[day]?.endTime || fallbackEnd,
        ),
      )
      .filter((minutes) => minutes > 0);

    return windows.length > 0 ? Math.min(...windows) : 0;
  }

  return calculateDurationInMinutes(
    getWatchedTime(watch, 'times.startTime'),
    getWatchedTime(watch, 'times.endTime'),
  );
};

type PrivateLessonOfferingSectionProps = {
  isPrivateLesson: boolean;
  toggleId: string;
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
};

export const PrivateLessonOfferingSection = ({
  isPrivateLesson,
  toggleId,
  register,
  errors,
  watch,
  setValue,
}: PrivateLessonOfferingSectionProps) => {
  const availabilityWindowMinutes = getPrivateLessonMaxDurationMinutes(watch);

  return (
    <div className='space-y-4 rounded-lg border border-border p-4'>
      <div className='flex items-center justify-between gap-2'>
        <div>
          <Label htmlFor={toggleId} className='cursor-pointer font-medium'>
            Private lesson offering
          </Label>
          <p className='text-sm text-muted-foreground'>
            Creates bookable private or semi-private slots instead of a group
            class roster. Use Capacity (1–3) for capacity per slot.
          </p>
        </div>
        <Switch
          id={toggleId}
          checked={isPrivateLesson}
          onCheckedChange={(enabled) => {
            setValue('displayOptions.isPrivateLesson', enabled);
            if (enabled) {
              setValue('displayOptions.allowTrialEnrollment', false);
              setValue('displayOptions.allowPortalEnrollment', false);
              setValue('displayOptions.includeInPublicSchedule', false);
              const currentSeats = Number(watch('classInfo.seats'));
              if (
                !Number.isFinite(currentSeats) ||
                currentSeats < 1 ||
                currentSeats > 3
              ) {
                setValue('classInfo.seats', 1);
              }
              const currentDuration = Number(watch('times.duration'));
              if (!currentDuration || currentDuration === availabilityWindowMinutes) {
                setValue('times.duration', 30);
              }
              if (!watch('times.slotInterval')) {
                setValue('times.slotInterval', 30);
              }
            }
          }}
        />
      </div>

      {isPrivateLesson && (
        <>
          <p className='text-sm text-muted-foreground'>
            Use the availability start and end times above to set when lessons
            can be booked. Lesson duration is how long each session lasts; slot
            interval is how often new slots can start. Set seats to 2–3 for
            semi-private lessons.
          </p>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <div className='space-y-2'>
              <Label>Lesson duration (minutes)</Label>
              <Input
                {...register('times.duration', {
                  required: isPrivateLesson
                    ? 'Lesson duration is required'
                    : false,
                  min: {
                    value: 1,
                    message: 'Lesson duration must be at least 1 minute',
                  },
                  validate: (value) => {
                    if (!isPrivateLesson) {
                      return true;
                    }

                    const lessonDuration = Number(value);
                    const maxWindow = getPrivateLessonMaxDurationMinutes(watch);
                    if (maxWindow > 0 && lessonDuration > maxWindow) {
                      return 'Lesson duration cannot exceed the availability window';
                    }

                    return true;
                  },
                  valueAsNumber: true,
                })}
                type='number'
                min={1}
                placeholder='30'
              />
              {typeof errors.times?.duration?.message === 'string' && (
                <span className='text-sm text-destructive'>
                  {errors.times.duration.message}
                </span>
              )}
            </div>
            <div className='space-y-2'>
              <Label>Slot interval (minutes)</Label>
              <Input
                {...register('times.slotInterval', {
                  required: isPrivateLesson
                    ? 'Slot interval is required'
                    : false,
                  min: {
                    value: 1,
                    message: 'Slot interval must be at least 1 minute',
                  },
                  valueAsNumber: true,
                })}
                type='number'
                min={1}
                placeholder='30'
              />
              {typeof errors.times?.slotInterval?.message === 'string' && (
                <span className='text-sm text-destructive'>
                  {errors.times.slotInterval.message}
                </span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
